import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Drawer,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {ResponsiveContainer } from 'recharts';
import { api } from '../../services/api';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { FREE_RESEARCH_LIMITS, SUBSCRIPTION_LIMITS } from '../../payment/PricingUtils';
import { useAuth } from '../../authentication/AuthContext';
import PersonaBarChart from '../../components/PersonaBarChart.js';


function Step2Personas({ userData, setUserData, onTopicsChange, setTabIndex }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState('age_range');
  const [currentPage, setCurrentPage] = useState(1);
  const [groupBy, setGroupBy] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Survey context state
  const [surveyContext, setSurveyContext] = useState('');
  const [originalSurveyContext, setOriginalSurveyContext] = useState('');
  const [contextSaving, setContextSaving] = useState(false);
  const [contextSaved, setContextSaved] = useState(false);
  const [contextError, setContextError] = useState('');
  const [showContextSuccess, setShowContextSuccess] = useState(false);
  
  const { isAdmin, hasActiveSubscription } = useAuth();
  // const { walletBalance } = useAuth();
  const isFreeResearch = !isAdmin && !hasActiveSubscription;

  // Get the maximum allowed personas based on user type
  const getMaxPersonas = () => {
    if (isAdmin) {
      return 200; // Admin limit (custom higher limit for admins)
    } else if (hasActiveSubscription) {
      return SUBSCRIPTION_LIMITS.MAX_PERSONAS; // 100 for subscription users
    } else {
      return FREE_RESEARCH_LIMITS.MAX_PERSONAS; // 20 for free users
    }
  };

  const maxPersonas = getMaxPersonas();

  const [filters, setFilters] = useState({
    numberOfPersonas: userData.personaFilters?.numberOfPersonas || Math.min(20, maxPersonas),
    gender: Array.isArray(userData.personaFilters?.gender) 
      ? userData.personaFilters.gender 
      : (userData.personaFilters?.gender ? [userData.personaFilters.gender] : []),
    ageRange: Array.isArray(userData.personaFilters?.age_range) 
      ? userData.personaFilters.age_range 
      : (userData.personaFilters?.age_range ? [userData.personaFilters.age_range] : []),
    employmentStatus: Array.isArray(userData.personaFilters?.employment_status)
      ? userData.personaFilters.employment_status 
      : (userData.personaFilters?.employment_status ? [userData.personaFilters.employment_status] : []),
    income: Array.isArray(userData.personaFilters?.income)
      ? userData.personaFilters.income 
      : (userData.personaFilters?.income ? [userData.personaFilters.income] : []),
    education: Array.isArray(userData.personaFilters?.education)
      ? userData.personaFilters.education 
      : (userData.personaFilters?.education ? [userData.personaFilters.education] : []),
    householdSituation: Array.isArray(userData.personaFilters?.household_situation)
      ? userData.personaFilters.household_situation 
      : (userData.personaFilters?.household_situation ? [userData.personaFilters.household_situation] : []),
    maritalStatus: Array.isArray(userData.personaFilters?.marital_status)
      ? userData.personaFilters.marital_status 
      : (userData.personaFilters?.marital_status ? [userData.personaFilters.marital_status] : [])
  });

  // Load survey context when component mounts or userData changes
  useEffect(() => {
    const loadSurveyContext = async () => {
      if (userData.userID && userData.researchID) {
        try {
          const research = await api.getPreviousResearch(userData.userID, userData.researchID);
          const contextFromServer = research?.survey?.survey_context || '';
          setSurveyContext(contextFromServer);
          setOriginalSurveyContext(contextFromServer);
          setContextSaved(true); // Initially saved since it matches server
        } catch (error) {
          console.error('Error loading survey context:', error);
        }
      }
    };

    loadSurveyContext();
  }, [userData.userID, userData.researchID]);

  // Check if personas count exceeds limit on initial load
  useEffect(() => {
    if (filters.numberOfPersonas > maxPersonas) {
      setFilters(prev => ({
        ...prev,
        numberOfPersonas: maxPersonas
      }));
      setError(`You are limited to ${maxPersonas} personas based on your current plan.`);
    }
  }, [filters.numberOfPersonas, maxPersonas]);

  // Check if context has been modified
  const contextModified = surveyContext !== originalSurveyContext;

  // Handle context text change
  const handleContextChange = (e) => {
    setSurveyContext(e.target.value);
    setContextSaved(false);
    setContextError('');
  };

  // Save context to server
  const handleSaveContext = async () => {
    if (!userData.userID || !userData.researchID) {
      setContextError('Missing user or research ID');
      return;
    }

    setContextSaving(true);
    setContextError('');

    try {
      await api.addContext(userData.userID, userData.researchID, surveyContext);
      setOriginalSurveyContext(surveyContext);
      setContextSaved(true);
      setShowContextSuccess(true);
      console.log('Survey context saved successfully');
    } catch (error) {
      // console.error('Error saving survey context:', error);
      setContextError('Failed to save survey context');
    } finally {
      setContextSaving(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (
      userData.selectedPersonas &&
      Object.keys(userData.selectedPersonas).length === 0 &&
      userData.personasStatus !== 'COMPLETED'
    ) {
      setDrawerOpen(true);
    }
  }, [userData.selectedPersonas, userData.personasStatus]);

  const personasPerPage = 10;
  
  // Check if numberOfPersonas exceeds limits for free research and adjust if needed
  useEffect(() => {
    if (isFreeResearch && filters.numberOfPersonas > FREE_RESEARCH_LIMITS.MAX_PERSONAS) {
      setFilters(prev => ({
        ...prev,
        numberOfPersonas: FREE_RESEARCH_LIMITS.MAX_PERSONAS
      }));
      setError(`Free research is limited to ${FREE_RESEARCH_LIMITS.MAX_PERSONAS} personas.`);
    }
  }, [filters.numberOfPersonas, isFreeResearch]);

  
const checkForChanges = () => {
  // If we don't have a previously saved state, there's nothing to compare against
  if (!userData.lastGenerationState) return true;
  
  const lastState = userData.lastGenerationState;
  
  // Check if number of questions has changed
  if (lastState.questionCount !== userData.surveyQuestions?.length) return true;
  
  // Check if number of personas has changed
  if (lastState.personaCount !== Object.keys(userData.selectedPersonas || {}).length) return true;
  
  // Check if question content has changed
  if (lastState.questions) {
    for (let i = 0; i < userData.surveyQuestions?.length; i++) {
      const currentQ = userData.surveyQuestions[i];
      const lastQ = lastState.questions.find(q => q.questionID === currentQ.questionID);
      
      if (!lastQ) return true; // New question
      if (lastQ.questionText !== currentQ.questionText) return true; // Question text changed
      if (lastQ.questionType !== currentQ.questionType) return true; // Question type changed
      
      // For multiple-choice questions, check if options changed
      if (currentQ.questionType === 'multiple-choice') {
        if (!arraysEqual(lastQ.responseOptions || [], currentQ.responseOptions || [])) return true;
      }
    }
  }
  
  return false;
};

// Helper function to compare arrays
const arraysEqual = (a, b) => {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

  const handleContinue = async () => {
    // Check if the context has been modified but not saved
    if (contextModified) {
      const saveFirst = window.confirm('You have unsaved changes to your survey context. Would you like to save them before continuing?');
      if (saveFirst) {
        await handleSaveContext();
      }
    }

    // If user has made changes to personas or survey and there are existing responses
    const hasExistingResponses = userData.responsesStatus === 'COMPLETED' && userData.surveyResponses;
    const hasChanges = checkForChanges(); // Your existing function to detect changes
    
    if (hasExistingResponses && hasChanges) {
      // Notify user they'll need to regenerate responses
      const confirmed = window.confirm(
        'There have been changes to your survey or personas. You will need to regenerate responses, which will use a research credit.\n\nDo you want to continue?'
      );
      
      if (!confirmed) {
        return;
      }
      
      // Mark as needing regeneration but don't charge yet
      setUserData(prev => ({
        ...prev,
        hasChangedSinceLastGeneration: true
      }));
    }
    
    // Move to responses tab
    setTabIndex(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Check if the number of personas exceeds the limit based on user type
    if (filters.numberOfPersonas > maxPersonas) {
      let errorMessage = '';
      if (isAdmin) {
        errorMessage = `Admin accounts are limited to ${maxPersonas} personas per research.`;
      } else if (hasActiveSubscription) {
        errorMessage = `Your subscription plan is limited to ${maxPersonas} personas per research.`;
      } else {
        errorMessage = `Free research is limited to ${maxPersonas} personas. Please reduce the number of personas or upgrade to a subscription plan.`;
      }
      
      setError(errorMessage);
      setLoading(false);
      return;
    }

    try {
      const personas = await api.selectPersonas({
        userID: userData.userID,
        researchID: userData.researchID,
        numberOfPersonas: filters.numberOfPersonas,
        gender: filters.gender,
        ageRange: filters.ageRange,
        employmentStatus: filters.employmentStatus,
        income: filters.income,
        education: filters.education,
        householdSituation: filters.householdSituation,
        maritalStatus: filters.maritalStatus
      });

      if (personas && Object.keys(personas).length >= filters.numberOfPersonas) {
        setUserData(prev => ({
          ...prev,
          selectedPersonas: personas,
          personaFilters: { ...filters },
          personasStatus: 'COMPLETED',
        }));

        if (onTopicsChange) {
          onTopicsChange();
        }
        setCurrentPage(1);
      } else {
        setError("Not enough valid personas could be created with the given filters. Please adjust your filters and try again.");
      }
    } catch (err) {
      setError(err.message);
      // console.error('Error details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const filteredPersonas = Object.entries(userData.selectedPersonas)
  .filter(([key, persona]) => {
    const personaValues = Object.values(persona).map(value => value.toLowerCase());
    return personaValues.some(value => value.includes(searchQuery.toLowerCase()));
  });

  const indexOfLastPersona = currentPage * personasPerPage;
  const indexOfFirstPersona = indexOfLastPersona - personasPerPage;
  const currentPersonas = filteredPersonas.slice(indexOfFirstPersona, indexOfLastPersona);

  const handleToggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const initialFilters = {
    numberOfPersonas: userData.personaFilters?.numberOfPersonas || 5,
    gender: [],
    ageRange: [],
    employmentStatus: [],
    income: [],
    education: [],
    householdSituation: [],
    maritalStatus: []
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  const handleCheckboxChange = (filterKey, value) => {
    setFilters((prev) => {
      const currentValues = prev[filterKey];
      if (currentValues.includes(value)) {
        return { ...prev, [filterKey]: currentValues.filter((item) => item !== value) };
      }
      return { ...prev, [filterKey]: [...currentValues, value] };
    });
  };

  const ageOrder = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
  const incomeOrder = [
    "<10000", "10000-19999", "20000-29999", "30000-39999",
    "40000-49999", "50000-99999", "100000-199999", ">200000"
  ];
  const educationOrder = [
    "Elementary", "Low High school", "MBO/ High school",
    "HBO/WO Bachelor", "Master or PHD"
  ];

  const sortByOrder = (data, order) => {
    return [...data].sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  };

  const calculatePercentages = (attribute) => {
    const totalCount = Object.values(userData.selectedPersonas).length;
    const counts = {};

    Object.values(userData.selectedPersonas).forEach(persona => {
      const key = persona[attribute];
      counts[key] = (counts[key] || 0) + 1;
    });

    let result = Object.entries(counts).map(([key, count]) => ({
      name: key,
      value: +(count / totalCount * 100).toFixed(1),
      count
    }));

    if (attribute === "age_range") {
      result = sortByOrder(result, ageOrder);
    } else if (attribute === "income") {
      result = sortByOrder(result, incomeOrder);
    } else if (attribute === "education") {
      result = sortByOrder(result, educationOrder);
    }

    return result;
  };

  const formatAttributeLabel = (attr) =>
  attr
    .replace(/_/g, ' ') 
    .replace(/\b\w/g, char => char.toUpperCase()); 

  const data = useMemo(() => ({
    age_range: calculatePercentages("age_range"),
    income: calculatePercentages("income"),
    education: calculatePercentages("education"),
    employment_status: calculatePercentages("employment_status"),
    gender: calculatePercentages("gender"),
    household_situation: calculatePercentages("household_situation"),
    marital_status: calculatePercentages("marital_status")
  }), [userData.selectedPersonas]);

  const prepareStackedData = (attribute, groupBy) => {

    if (!groupBy) return data[attribute];

    const groupedData = {};

    Object.values(userData.selectedPersonas).forEach(persona => {
      const attrValue = persona[attribute];
      const groupValue = persona[groupBy];

      if (!groupedData[attrValue]) {
        groupedData[attrValue] = { name: attrValue };
      }

      groupedData[attrValue][groupValue] = (groupedData[attrValue][groupValue] || 0) + 1;
    });

    let result = Object.values(groupedData);

    if (attribute === "age_range") {
      result = sortByOrder(result, ageOrder);
    } else if (attribute === "income") {
      result = sortByOrder(result, incomeOrder);
    } else if (attribute === "education") {
      result = sortByOrder(result, educationOrder);
    }

    console.log('Prepared stacked data:', result);

    return result;
  };




  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Select Personas
        </Typography>

        {/* Alert for user's plan limits */}
        {isAdmin ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Admin accounts can use up to {maxPersonas} personas per research.
          </Alert>
        ) : hasActiveSubscription ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your subscription allows up to {maxPersonas} personas per research.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Free research is limited to {maxPersonas} personas maximum. Upgrade to a subscription for up to {SUBSCRIPTION_LIMITS.MAX_PERSONAS} personas.
          </Alert>
        )}

        {/* Survey Context Section */}
        <Paper sx={{ p: 3, mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Add survey introduction for personas:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={surveyContext}
            onChange={handleContextChange}
            placeholder="Add context to your survey that will be given at the start of the survey making process for each of our personas"
            variant="outlined"
            sx={{ mt: 1, mb: 2 }}
            helperText="This context will be provided to all personas before they answer the survey questions. This can include background information, facts you want to be true about the persona, or any other relevant details that will help the personas understand the survey better."
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveContext}
              disabled={contextSaving || (!contextModified && contextSaved)}
              size="small"
            >
              {contextSaving ? <CircularProgress size={16} /> : 'Save Context'}
            </Button>
            
            {contextSaved && !contextModified && (
              <Typography variant="body2" color="success.main">
                Context saved
              </Typography>
            )}
            
            {contextModified && (
              <Typography variant="body2" color="text.secondary">
                Unsaved changes
              </Typography>
            )}
            
            {contextError && (
              <Typography variant="body2" color="error">
                {contextError}
              </Typography>
            )}
          </Box>
        </Paper>

        <Drawer anchor="right" open={drawerOpen} onClose={handleToggleDrawer(false)}>
          <Box sx={{ width: 300, p: 2 }}>
            <IconButton
              onClick={handleToggleDrawer(false)}
              sx={{ position: 'absolute', top: 14, right: 8 }}
            >
              <CloseIcon />
            </IconButton>

            <Typography variant="h6" sx={{mb: 4}}>Filter Personas</Typography>

            <TextField
              fullWidth
              type="number"
              label="Number of Personas"
              value={filters.numberOfPersonas}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                
                if (value > maxPersonas) {
                  let errorMessage = '';
                  if (isAdmin) {
                    errorMessage = `Admin accounts are limited to ${maxPersonas} personas.`;
                  } else if (hasActiveSubscription) {
                    errorMessage = `Your subscription plan is limited to ${maxPersonas} personas.`;
                  } else {
                    errorMessage = `Free research is limited to ${maxPersonas} personas. Please upgrade for more.`;
                  }
                  setError(errorMessage);
                  return;
                }
                
                setFilters(prev => ({ ...prev, numberOfPersonas: value }));
                setError(null); // Clear error when valid value is entered
              }}
              InputProps={{
                inputProps: { 
                  min: 1, 
                  max: maxPersonas
                }
              }}
              margin="normal"
              helperText={`Maximum ${maxPersonas} personas for your plan`}
            />

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset">
              <FormGroup>
                <Typography sx={{ fontWeight: 'bold', mb: 1}}>Gender</Typography>
                {["Man", "Woman"].map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={filters.gender.includes(option)}
                        onChange={() => handleCheckboxChange("gender", option)}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset">
              <FormGroup>
                <Typography sx={{ fontWeight: 'bold', mb: 1}}>Age Range</Typography>
                {["18-24", "25-34", "35-44", "45-54", "55-64", "65+"].map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={filters.ageRange.includes(option)}
                        onChange={() => handleCheckboxChange("ageRange", option)}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
            </FormControl>
            
            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset">
              <FormGroup>
                <Typography sx={{ fontWeight: 'bold', mb: 1}}>Employment Status</Typography>
                {["Independent", "Part-time", "Full-time", "Unemployed"].map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={filters.employmentStatus.includes(option)}
                        onChange={() => handleCheckboxChange("employmentStatus", option)}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset">
              <FormGroup>
                <Typography sx={{ fontWeight: 'bold', mb: 1}}>Income</Typography>
                {["<10000", "10000-19999", "20000-29999", "30000-39999", "40000-49999", "50000-99999", "100000-199999", ">200000"].map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={filters.income.includes(option)}
                        onChange={() => handleCheckboxChange("income", option)}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset">
              <FormGroup>
                <Typography sx={{ fontWeight: 'bold', mb: 1}}>Education</Typography>
                {["Elementary", "Low High school", "MBO/ High school", "HBO/WO Bachelor", "Master or PHD"].map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={filters.education.includes(option)}
                        onChange={() => handleCheckboxChange("education", option)}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset">
              <FormGroup>
                <Typography sx={{ fontWeight: 'bold', mb: 1}}>Household Situation</Typography>
                {["Kids living at home", "Living alone", "Living together without kids unmarried", "Living together married without kids",
                  "Living together with kids unmarried", "Living together married with kids", "Parent living alone", "Other",
                  "Person in institutional housing"].map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={filters.householdSituation.includes(option)}
                          onChange={() => handleCheckboxChange("householdSituation", option)}
                        />
                      }
                      label={option}
                    />
                ))}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset">
              <FormGroup>
                <Typography sx={{ fontWeight: 'bold', mb: 1}}>Marital Status</Typography>
                {["Unmarried", "Married", "Widowed", "Divorced"].map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={filters.maritalStatus.includes(option)}
                        onChange={() => handleCheckboxChange("maritalStatus", option)}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="outlined" onClick={handleClearFilters}>Clear All</Button>
              <Button variant="contained" onClick={handleToggleDrawer(false)}>Apply</Button>
            </Box>
          </Box>
        </Drawer>

        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              Error: {error}
            </Typography>
          )}

          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ mt: 2, mr: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : (userData.selectedPersonas && Object.keys(userData.selectedPersonas).length > 0 ? 'Regenerate Personas' : 'Generate Personas')}
          </Button>

          <Button onClick={handleToggleDrawer(true)} variant="outlined" sx={{ mt: 2 }}>
            Customize Generated Personas
          </Button>
        </Box>

        {userData.selectedPersonas && Object.keys(userData.selectedPersonas).length > 0 && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <ToggleButtonGroup
            value={selectedAttribute}
            exclusive
            onChange={(e, newValue) => newValue && setSelectedAttribute(newValue)}
            sx={{
                mb: 4,
                flexWrap: 'wrap',
                width: '100%',
                '& .MuiToggleButton-root': {
                  border: '1px solid', 
                  borderColor: 'divider',
                  '&:not(:first-of-type)': {
                  },
                },
              }}
          >
            <ToggleButton value="gender">Gender</ToggleButton>
            <ToggleButton value="age_range">Age</ToggleButton>
            <ToggleButton value="employment_status">Employment</ToggleButton>
            <ToggleButton value="income">Income</ToggleButton>
            <ToggleButton value="education">Education</ToggleButton>
            <ToggleButton value="household_situation">Household</ToggleButton>
            <ToggleButton value="marital_status">Marital</ToggleButton>
          </ToggleButtonGroup>

          <ResponsiveContainer width='100%' height={500}>
            <PersonaBarChart
              data={data}
              selectedAttribute={selectedAttribute}
              groupBy={groupBy}
              prepareStackedData={prepareStackedData}
              formatAttributeLabel={formatAttributeLabel}
            />
          </ResponsiveContainer>

          <Accordion sx={{ mt: 3, borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">Advanced Analysis</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Select a grouping attribute to analyze personas in more detail.
                </Typography>

                <TextField
                  select
                  label="Group by"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="gender">Gender</MenuItem>
                  <MenuItem value="age_range">Age</MenuItem>
                  <MenuItem value="employment_status">Employment Status</MenuItem>
                  <MenuItem value="income">Income</MenuItem>
                  <MenuItem value="education">Education</MenuItem>
                  <MenuItem value="household_situation">Household Situation</MenuItem>
                  <MenuItem value="marital_status">Marital Status</MenuItem>
                </TextField>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
        )}

        {userData.selectedPersonas && Object.keys(userData.selectedPersonas).length > 0 && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Pagination
              count={Math.ceil(Object.keys(filteredPersonas).length / personasPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              sx={{ mt: 2 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, mt: 1 }}>
              <TextField
                label="Search"
                variant="outlined"
                size="small" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: '200px', mr: 2, mb: 2 }} 
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
  {currentPersonas.length > 0 ? (
    currentPersonas.map(([key, persona]) => (
    <Grid item xs={12} sm={6} key={key} sx={{ display: 'flex' }}>
      <Card sx={{ mb: 2, flexGrow: 1 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{mb: 2}}>
            {persona.name || `Persona ${key}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ fontWeight: 'bold', ml: 1 }}>Gender:</Typography>
            <Typography sx={{ ml: 1 }}>{persona.gender}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ ml: 1, fontWeight: 'bold' }}>Age:</Typography> 
            <Typography sx={{ ml: 1 }}>{persona.age_range}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ ml: 1, fontWeight: 'bold' }}>Employment Status:</Typography> 
            <Typography sx={{ ml: 1 }}>{persona.employment_status}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ ml: 1, fontWeight: 'bold' }}>Income:</Typography> 
            <Typography sx={{ ml: 1 }}>{persona.income}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ ml: 1, fontWeight: 'bold' }}>Education:</Typography> 
            <Typography sx={{ ml: 1 }}>{persona.education}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ ml: 1, fontWeight: 'bold' }}>Household Situation:</Typography>
            <Typography sx={{ ml: 1 }}>{persona.household_situation}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ ml: 1, fontWeight: 'bold' }}>Marital Status:</Typography>
            <Typography sx={{ ml: 1 }}>{persona.marital_status}</Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  ))
) : (
  <Typography variant="body1" sx={{ mt: 2, mb: 4, width: '100%', textAlign: 'center' }}>
    No personas match your search.
  </Typography>
)}
</Grid>
</Paper>
)}

<Button
  variant="contained"
  onClick={handleContinue}
  sx={{ mt: 2, display: 'block', mx: 'auto' }}
>
  Continue
</Button>

{/* Context Save Success Snackbar */}
<Snackbar
  open={showContextSuccess}
  autoHideDuration={3000}
  onClose={() => setShowContextSuccess(false)}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
>
  <Alert onClose={() => setShowContextSuccess(false)} severity="success">
    Survey context saved successfully!
  </Alert>
</Snackbar>
</Box>
</ThemeProvider>
);
}

export default Step2Personas;